import { useField } from 'formik';
import * as React from 'react';
import { Tick, ChevronsUpDown, Cross } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface Option {
    label: string;
    value: string;
}

interface FormMultiSelectProps {
    name: string;
    label?: string;
    helpText?: string;
    placeholder?: string;
    options: Option[];
    className?: string;
    disabled?: boolean;
    required?: boolean;
    validate?: (value: unknown) => string | undefined;
}

export const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
    name,
    label,
    helpText,
    placeholder = 'Selecione...',
    options,
    className,
    disabled,
    required,
    validate,
}) => {
    const validateFn = (value: unknown) => {
        if (required && (!value || (Array.isArray(value) && value.length === 0))) {
            return 'Campo obrigatório';
        }
        if (validate) return validate(value);
        return undefined;
    };

    const [field, meta, helpers] = useField({ name, validate: validateFn });
    const [open, setOpen] = React.useState(false);

    const selectedValues = (Array.isArray(field.value) ? field.value : []) as string[];

    const handleSelect = (currentValue: string) => {
        const newValues = selectedValues.includes(currentValue)
            ? selectedValues.filter((value) => value !== currentValue)
            : [...selectedValues, currentValue];
        helpers.setValue(newValues);
    };

    const handleRemove = (valueToRemove: string, e: React.MouseEvent) => {
        if (disabled) return;
        e.stopPropagation();
        const newValues = selectedValues.filter((value) => value !== valueToRemove);
        helpers.setValue(newValues);
    };

    return (
        <div className={cn('space-y-2', className)}>
            {label && (
                <div className="space-y-1">
                    <Label className={cn(meta.touched && meta.error && 'text-destructive')}>
                        {label} {required ? '*' : ''}
                    </Label>
                    {helpText ? (
                        <p className="text-sm leading-relaxed text-muted-foreground">{helpText}</p>
                    ) : null}
                </div>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'h-auto min-h-10 w-full justify-between hover:bg-background',
                            meta.touched && meta.error && 'border-destructive',
                            disabled && 'cursor-not-allowed opacity-50',
                        )}
                    >
                        <div className="flex flex-wrap gap-1">
                            {selectedValues.length > 0 ? (
                                selectedValues.map((value) => {
                                    const option = options.find((o) => o.value === value);
                                    return (
                                        <Badge
                                            key={value}
                                            variant="secondary"
                                            className="mb-1 mr-1"
                                        >
                                            {option?.label || value}
                                            {!disabled && (
                                                <div
                                                    className="ml-1 cursor-pointer rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                                    onClick={(e) => handleRemove(value, e)}
                                                >
                                                    <Cross className="size-3 text-muted-foreground hover:text-foreground" />
                                                </div>
                                            )}
                                        </Badge>
                                    );
                                })
                            ) : (
                                <span className="font-normal text-muted-foreground">
                                    {placeholder}
                                </span>
                            )}
                        </div>
                        <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                        <CommandInput placeholder="Pesquisar..." />
                        <CommandList>
                            <CommandEmpty>Nenhum item encontrado.</CommandEmpty>
                            <CommandGroup>
                                {options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => handleSelect(option.value)}
                                    >
                                        <div
                                            className={cn(
                                                'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                                                selectedValues.includes(option.value)
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'opacity-50 [&_svg]:invisible',
                                            )}
                                        >
                                            <Tick className={cn('size-4')} />
                                        </div>
                                        {option.label}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {meta.touched && meta.error ? (
                <p className="text-[0.8rem] font-medium text-destructive">{meta.error}</p>
            ) : null}
        </div>
    );
};
